var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'root/src/client/v2/common/components/ui/sonner';
// Reusable debounced auto-save: call scheduleSave() from user-edit handlers (never from the
// seed/load effect) for auto-save, and save() for an explicit Save button. Exposes the
// loading/success/error states for a Button (loading/success/error props).
export const useAutoSave = ({ onSave, delay = 800, loadingMessage, successMessage, errorMessage }) => {
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const [failed, setFailed] = useState(false);
    const timerRef = useRef(null);
    const onSaveRef = useRef(onSave);
    onSaveRef.current = onSave;
    const save = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        setSaving(true);
        setFailed(false);
        const toastId = loadingMessage ? toast.loading(loadingMessage) : undefined;
        try {
            yield onSaveRef.current();
            setSaved(true);
            setTimeout(() => setSaved(false), 1300);
            if (successMessage) {
                toast.success(successMessage, toastId ? { id: toastId } : undefined);
            }
            else if (toastId) {
                toast.dismiss(toastId);
            }
        }
        catch (e) {
            setFailed(true);
            setTimeout(() => setFailed(false), 1300);
            if (errorMessage) {
                toast.error(errorMessage, toastId ? { id: toastId } : undefined);
            }
            else if (toastId) {
                toast.dismiss(toastId);
            }
        }
        finally {
            setSaving(false);
        }
    }), [loadingMessage, successMessage, errorMessage]);
    const scheduleSave = useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => { void save(); }, delay);
    }, [save, delay]);
    useEffect(() => () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, []);
    return { saving, saved, failed, save, scheduleSave };
};
